import { createAppAsyncThunk } from "Store/createAppAsyncThunk";

import { selectCarbonEmissionsCSVData } from "../carbonEmissions.selectors";

interface DownloadCSVArgs {
  organizationId: string;
}

/**
 * Thunk to handle CSV download with proper Redux architecture
 * - Gets data from Redux store via selector
 * - Handles the side effect of triggering browser download
 */
export const downloadCarbonEmissionsCSVThunk = createAppAsyncThunk(
  "carbonEmissions/downloadCSV",
  async ({ organizationId }: DownloadCSVArgs, { getState }) => {
    const state = getState();

    // Get CSV data from selector (business logic is in selector)
    const csvData = selectCarbonEmissionsCSVData(state, { organizationId });

    if (!csvData || csvData.length === 0) {
      throw new Error("No carbon emissions data available for download");
    }

    // Generate CSV content
    const headers = [
      "date: Month",
      "project_id",
      "project_title",
      "organization_id",
      "Sum of total_daily_kgCO2e"
    ];

    const csvRows = [
      headers.join(","),
      ...csvData.map(record =>
        [
          `"${record.date}"`,
          record.project_id,
          record.project_title,
          record.organization_id,
          record.total_daily_kgCO2e.toFixed(2)
        ].join(",")
      )
    ];

    const csvContent = csvRows.join("\n");

    // Generate filename with current date
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const filename = `carbon-emissions-${year}-${month}-${day}.csv`;

    // Trigger browser download (side effect)
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }

    return { filename, recordCount: csvData.length };
  }
);
